import React, { useState, useEffect, useCallback } from 'react';
import Modal from '../Modal';
import { saudeService } from '../../services/saude.service';
import { formatCPF } from '../../utils/formatters';
import type { EmpresaRepresentanteAtestadoContract } from '@sstexames/shared-types';
import {
  IdentificationIcon,
  PlusIcon,
  TrashIcon,
  StarIcon,
  PencilSquareIcon,
  DocumentCheckIcon,
  CheckCircleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';

interface ConfigurarRepresentanteModalProps {
  isOpen: boolean;
  onClose: () => void;
  empresaId: number;
  empresaNome?: string;
  toast: {
    success: (msg: string) => void;
    error: (msg: string) => void;
  };
}

export default function ConfigurarRepresentanteModal({
  isOpen,
  onClose,
  empresaId,
  empresaNome,
  toast,
}: ConfigurarRepresentanteModalProps) {
  const [representantes, setRepresentantes] = useState<EmpresaRepresentanteAtestadoContract[]>([]);
  const [loading, setLoading] = useState(false);
  const [salvando, setSalvando] = useState(false);

  // Form State
  const [idEditando, setIdEditando] = useState<number | null>(null);
  const [nome, setNome] = useState('');
  const [cargo, setCargo] = useState('');
  const [documento, setDocumento] = useState('');
  const [isPadrao, setIsPadrao] = useState(false);

  const limparFormulario = useCallback(() => {
    setIdEditando(null);
    setNome('');
    setCargo('');
    setDocumento('');
    setIsPadrao(false);
  }, []);

  // Carregar lista de representantes
  const carregarRepresentantes = useCallback(async () => {
    if (!empresaId) return;
    setLoading(true);
    try {
      const dados = await saudeService.obterRepresentantes(empresaId);
      setRepresentantes(dados);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Falha ao buscar representantes';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [empresaId, toast]);

  useEffect(() => {
    if (isOpen) {
      carregarRepresentantes();
      limparFormulario();
    }
  }, [isOpen, carregarRepresentantes, limparFormulario]);

  const handleDocumentoChange = (valor: string) => {
    const raw = valor.replace(/\D/g, '');
    if (raw.length <= 11) {
      setDocumento(formatCPF(raw));
    } else {
      setDocumento(valor);
    }
  };

  const handleEditar = (rep: EmpresaRepresentanteAtestadoContract) => {
    setIdEditando(rep.id);
    setNome(rep.nome);
    setCargo(rep.cargo || '');
    setDocumento(rep.documento || '');
    setIsPadrao(rep.is_padrao);
  };

  const handleSalvar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nome.trim()) {
      toast.error('Informe o nome completo do representante.');
      return;
    }
    if (!cargo.trim()) {
      toast.error('Informe o cargo ou função do representante.');
      return;
    }
    if (!documento.trim()) {
      toast.error('Informe o documento (CPF ou RG) do representante.');
      return;
    }

    setSalvando(true);
    try {
      await saudeService.salvarRepresentante({
        id: idEditando || undefined,
        empresa_id: empresaId,
        nome: nome.trim(),
        cargo: cargo.trim(),
        documento: documento.trim(),
        is_padrao: isPadrao,
      });

      toast.success(
        idEditando
          ? 'Representante atualizado com sucesso!'
          : 'Novo representante cadastrado com sucesso!',
      );
      limparFormulario();
      await carregarRepresentantes();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erro ao salvar representante.';
      toast.error(msg);
    } finally {
      setSalvando(false);
    }
  };

  const handleExcluir = async (id: number) => {
    if (!confirm('Deseja realmente remover este representante?')) return;
    try {
      await saudeService.excluirRepresentante(id, empresaId);
      toast.success('Representante excluído com sucesso.');
      if (idEditando === id) limparFormulario();
      await carregarRepresentantes();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erro ao excluir representante.';
      toast.error(msg);
    }
  };

  const handleDefinirPadrao = async (id: number) => {
    try {
      await saudeService.definirRepresentantePadrao(id, empresaId);
      toast.success('Representante definido como padrão para os ofícios.');
      await carregarRepresentantes();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erro ao definir representante padrão.';
      toast.error(msg);
    }
  };

  // Representante em preview (do formulário ou o padrão atual)
  const repPreviewNome = nome || (representantes.find((r) => r.is_padrao)?.nome) || 'Nome do Representante';
  const repPreviewCargo = cargo || (representantes.find((r) => r.is_padrao)?.cargo) || 'Gestão de Recursos Humanos';
  const repPreviewDoc = documento || (representantes.find((r) => r.is_padrao)?.documento) || '000.000.000-00';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Configuração de Assinatura & Representante de Ofício"
      size="xl"
    >
      <div className="space-y-6">
        {/* Banner Informativo */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
          <InformationCircleIcon className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
          <div className="text-xs text-blue-800 leading-relaxed">
            <span className="font-bold">Emissão Formal de Ofício: </span>
            Ao solicitar verificação de atestados médicos junto a UPAs, UBSs, Hospitais e Clínicas, o sistema gera automaticamente um documento formal em PDF timbrado (SST Exames & D+ Saúde) com os dados do colaborador e a máscara de assinatura do representante legal da empresa.
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Formulário de Cadastro / Edição */}
          <div className="lg:col-span-7 bg-white rounded-xl border border-gray-200 p-5 shadow-2xs space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-gray-100">
              <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                <IdentificationIcon className="w-4 h-4 text-gray-700" />
                {idEditando ? 'Editar Representante' : 'Novo Representante'}
              </h3>
              {idEditando && (
                <button
                  type="button"
                  onClick={limparFormulario}
                  className="text-xs text-gray-500 hover:text-gray-800 font-medium underline"
                >
                  Cancelar Edição
                </button>
              )}
            </div>

            <form onSubmit={handleSalvar} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Nome Completo <span className="text-rose-500 font-bold">*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="Ex: Carlos Alberto Souza"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  className="w-full text-xs rounded-lg border-gray-300 focus:border-gray-900 focus:ring-gray-900 py-2 px-3 shadow-2xs"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Cargo / Função <span className="text-rose-500 font-bold">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Ex: Gerente de RH / DP"
                    value={cargo}
                    onChange={(e) => setCargo(e.target.value)}
                    className="w-full text-xs rounded-lg border-gray-300 focus:border-gray-900 focus:ring-gray-900 py-2 px-3 shadow-2xs"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Documento (CPF / RG) <span className="text-rose-500 font-bold">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="000.000.000-00 ou RG"
                    value={documento}
                    onChange={(e) => handleDocumentoChange(e.target.value)}
                    className="w-full text-xs rounded-lg border-gray-300 focus:border-gray-900 focus:ring-gray-900 py-2 px-3 shadow-2xs"
                  />
                </div>
              </div>

              <div className="pt-1">
                <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={isPadrao}
                    onChange={(e) => setIsPadrao(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                  />
                  <span className="text-xs text-gray-700 font-medium">
                    Definir como representante padrão para todos os ofícios emitidos
                  </span>
                </label>
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                {idEditando && (
                  <button
                    type="button"
                    onClick={limparFormulario}
                    className="px-3 py-2 rounded-lg border border-gray-200 text-gray-700 text-xs font-semibold hover:bg-gray-50 transition"
                  >
                    Descartar
                  </button>
                )}
                <button
                  type="submit"
                  disabled={salvando}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900 hover:bg-gray-800 text-white text-xs font-bold shadow-2xs transition disabled:opacity-50"
                >
                  <PlusIcon className="w-3.5 h-3.5" />
                  {salvando ? 'Salvando...' : idEditando ? 'Salvar Alterações' : 'Adicionar Representante'}
                </button>
              </div>
            </form>
          </div>

          {/* Prévia da Máscara de Assinatura */}
          <div className="lg:col-span-5 bg-gradient-to-b from-gray-50 to-white rounded-xl border border-gray-200 p-5 shadow-2xs flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-2 text-xs font-bold text-gray-800 pb-3 border-b border-gray-200">
                <DocumentCheckIcon className="w-4 h-4 text-emerald-600" />
                <span>Prévia da Máscara no Ofício (PDF)</span>
              </div>
              <p className="text-[11px] text-gray-500 mt-2 mb-4 leading-normal">
                Esta máscara de assinatura é impressa no rodapé do documento formal de solicitação encaminhado à unidade de saúde:
              </p>

              {/* Caixa simulação timbrado */}
              <div className="bg-white border border-dashed border-gray-300 rounded-lg p-5 text-center shadow-inner space-y-2">
                <div className="w-48 h-0.5 bg-gray-400 mx-auto mt-6 mb-3" />
                <div className="text-xs font-bold text-gray-900 uppercase tracking-wide">
                  {repPreviewNome}
                </div>
                <div className="text-[11px] text-gray-600">
                  {repPreviewCargo}
                </div>
                <div className="text-[10px] text-gray-500 font-mono">
                  Documento: {repPreviewDoc}
                </div>
                <div className="text-[11px] text-gray-800 font-semibold pt-1">
                  {empresaNome || 'Nome da Empresa'}
                </div>
                <div className="text-[9px] text-gray-400 italic">
                  Representante Credenciado da Empresa
                </div>
              </div>
            </div>

            <div className="mt-4 pt-3 border-t border-gray-100 flex items-center gap-2 text-[11px] text-gray-500">
              <CheckCircleIcon className="w-4 h-4 text-emerald-500 shrink-0" />
              <span>Geração automática e vinculada por protocolo eletrônico.</span>
            </div>
          </div>
        </div>

        {/* Tabela de Representantes Cadastrados */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-2xs">
          <div className="px-5 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
            <h4 className="text-xs font-bold text-gray-800 uppercase tracking-wider">
              Representantes Cadastrados ({representantes.length})
            </h4>
          </div>

          {loading ? (
            <div className="p-8 text-center text-xs text-gray-500">
              Carregando representantes...
            </div>
          ) : representantes.length === 0 ? (
            <div className="p-8 text-center text-xs text-gray-500">
              Nenhum representante cadastrado para esta empresa ainda. Preencha os campos acima para cadastrar o primeiro.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-gray-50 text-gray-600 font-semibold border-b border-gray-200">
                  <tr>
                    <th className="py-2.5 px-4">Nome</th>
                    <th className="py-2.5 px-4">Cargo / Função</th>
                    <th className="py-2.5 px-4">Documento</th>
                    <th className="py-2.5 px-4 text-center">Padrão</th>
                    <th className="py-2.5 px-4 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {representantes.map((rep) => (
                    <tr key={rep.id} className="hover:bg-gray-50/70 transition">
                      <td className="py-3 px-4 font-semibold text-gray-900">
                        {rep.nome}
                      </td>
                      <td className="py-3 px-4 text-gray-600">
                        {rep.cargo || '—'}
                      </td>
                      <td className="py-3 px-4 text-gray-600 font-mono">
                        {rep.documento || '—'}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {rep.is_padrao ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200">
                            <StarSolidIcon className="w-3 h-3 text-amber-600" />
                            Padrão
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleDefinirPadrao(rep.id)}
                            className="inline-flex items-center gap-1 text-[11px] text-gray-400 hover:text-amber-600 font-medium transition"
                            title="Tornar este representante o padrão"
                          >
                            <StarIcon className="w-3.5 h-3.5" />
                            Definir
                          </button>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="inline-flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleEditar(rep)}
                            className="p-1.5 rounded-lg text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition"
                            title="Editar representante"
                          >
                            <PencilSquareIcon className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleExcluir(rep.id)}
                            className="p-1.5 rounded-lg text-rose-500 hover:text-rose-700 hover:bg-rose-50 transition"
                            title="Remover representante"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
